/**
 * German hyphenation (TeX / Typst-compatible Liang patterns via `hyphen/de`).
 * Same family of patterns as https://typst.app/tools/hyphenate/ (German).
 *
 * For react-pdf: return syllable parts only (no soft hyphens in the parts).
 * Soft hyphens must NOT be injected into visible PDF/HTML text — many fonts
 * render U+00AD as a mid-word space even when the line does not break.
 */
import { hyphenateSync as hyphenateDe } from 'hyphen/de';

const SOFT_HYPHEN = '\u00ad';
const NO_BREAK_SPACE = '\u00a0';

/** Strip soft hyphens from a string. */
function stripSoftHyphens(s: string): string {
  return s.split(SOFT_HYPHEN).join('');
}

/**
 * Split a token into syllables for react-pdf.
 * Visible "-" is drawn by react-pdf only when a line actually breaks.
 * Joined parts must equal the original word (minus any existing soft hyphens).
 *
 * A soft hyphen the author typed is an instruction, not noise: SQUAD asked to be
 * able to place a break by hand when the automatic one reads badly in a narrow
 * column. Those positions therefore win outright — the word breaks where the author
 * said and nowhere else. The soft hyphen itself is still kept out of the parts, so
 * it can never be painted as a mid-word space.
 */
export function germanHyphenateWord(word: string | null | undefined): string[] {
  if (word == null) return [];
  // Preserve whitespace tokens for Knuth–Plass glue; never invent breaks there
  if (word === '' || /^\s+$/.test(word)) return [word];
  if (!/[A-Za-zÄÖÜäöüß]/.test(word)) return [word];

  if (word.includes(SOFT_HYPHEN)) {
    const authored = word.split(SOFT_HYPHEN).filter((part) => part.length > 0);
    if (authored.length > 1) return authored;
  }

  const expected = stripSoftHyphens(word);
  if (expected.length < 4) return [word];

  let soft: string;
  try {
    soft = hyphenateDe(expected);
  } catch {
    return [word];
  }

  // Plain split — do not leave U+00AD in parts (fonts may paint them as spaces)
  const parts = soft.split(SOFT_HYPHEN).filter((p) => p.length > 0);

  if (parts.length <= 1) return [word];
  if (parts.join('') !== expected) return [word];

  // Also allow a break after an existing hard hyphen, without empty fragments
  const withHardBreaks = parts.flatMap((part) => {
    if (!/[-–—]/.test(part)) return [part];
    const chunks = part.split(/(?<=[-–—])/).filter((p) => p.length > 0);
    return chunks.length ? chunks : [part];
  });

  if (withHardBreaks.join('') !== expected) return parts;
  return withHardBreaks;
}

/** Tokens where a break inside the word would corrupt the content. */
const URL_LIKE = /:\/\/|@|^www\.|\.(?:de|com|lu|eu|net|org)(?:\/|$)/i;

/**
 * Split a token into fixed-length chunks — last resort for strings that have no
 * hyphenation points (URLs, ISINs, digit runs). Never detaches trailing punctuation.
 */
export function chunkLongToken(word: string, maxChunk: number): string[] {
  if (!word || word.length <= maxChunk) return [word];

  const punctMatch = word.match(/^([\s\S]*?)([.,;:!?%））\]»”']+)$/);
  const core = punctMatch ? punctMatch[1] : word;
  const punct = punctMatch ? punctMatch[2] : '';

  if (!core || core.length <= maxChunk) return [word];

  const parts: string[] = [];
  for (let i = 0; i < core.length; i += maxChunk) {
    const chunk = core.slice(i, i + maxChunk);
    const isLast = i + maxChunk >= core.length;
    parts.push(isLast ? chunk + punct : chunk);
  }
  return parts.length ? parts : [word];
}

export const germanSafeWrapCallback = (word: string | null, maxChunk = 14): string[] => {
  if (!word) return word == null ? [] : [word];

  // Never break URLs/mails — chunking would still split them (and react-pdf may paint a hyphen)
  if (URL_LIKE.test(word)) return [word];

  /*
   * A protected space ties two words into one unit, and react-pdf honours that: it
   * breaks on U+0020 only, so the unit arrives here as a single word. Offering points
   * inside it would undo the author's instruction in the worst possible way — "31. De-
   * zember" became "3" / "1. Dezember" before this guard. An overlong unit is the
   * author's call, exactly as it is in Word.
   */
  if (word.includes(NO_BREAK_SPACE)) return [word];

  const syllables = germanHyphenateWord(word).filter((part) => part !== '');
  if (syllables.length > 1) {
    /*
     * An author who places one hyphen in a long compound switches the automatic points
     * off, and a remaining fragment can still be wider than the column. Only what is
     * still oversized gets further points — by syllable where the patterns find any,
     * by blunt chunking otherwise. The author's own positions are never removed.
     */
    return syllables.flatMap((part) => {
      if (part.length <= maxChunk) return [part];
      const finer = germanHyphenateWord(part).filter((piece) => piece !== '');
      return finer.length > 1 ? finer : chunkLongToken(part, maxChunk);
    });
  }

  return chunkLongToken(word, maxChunk);
};

/**
 * @deprecated Do not inject soft hyphens into pdfmake/HTML — they often render as spaces.
 * Kept for callers that need syllable-aware processing only.
 */
export function insertGermanSoftHyphens(text: string): string {
  // No-op: soft-hyphen injection caused mid-word spaces in generated PDFs.
  return text;
}

export { SOFT_HYPHEN };
